<template>
  <div class="tab config-tab">
    <Collapsible class="advanced-collapsible" :label="$t('general-style')">
      <ColorPicker
        :label="$t('background-color')"
        :color="options.backgroundColor"
        @submit="propChanged('backgroundColor', $event)"
      />

      <div class="advanced-collapsible__item">
        <strong>{{ $t('elements-margin') }} (px)</strong>

        <PlacementNumber
          :initial="options.itemStyle.general.margin"
          @change="propChanged('itemStyle.general.margin', $event)"
        />
      </div>

      <Spacement
        :label="$t('padding')"
        :options="options.padding"
        class="advanced-collapsible__item"
        @change="propChanged(`padding.${$event.field}`, $event.value)"
      />

      <Border
        :border="options.border"
        class="advanced-collapsible__item"
        @change="propChanged(`border.${$event.field}`, $event.value)"
      />
    </Collapsible>

    <Collapsible class="advanced-collapsible" :label="$t('label-style')">
      <div class="advanced-collapsible__item">
        <strong>{{ $t('positioning') }}</strong>
        <Dropdown
          :options="[
            { value: 'top', text: $t('above') },
            { value: 'side', text: $t('side') },
          ]"
          :value="options.itemStyle.general.labelOn"
          @change="propChanged('itemStyle.general.labelOn', $event)"
        />
      </div>

      <div class="advanced-collapsible__item">
        <strong>{{ $t('size') }} (px)</strong>
        <PlacementNumber
          :initial="options.itemStyle.general.labelSize"
          @change="propChanged('itemStyle.general.labelSize', $event)"
        />
      </div>

      <ColorPicker
        :label="$t('color')"
        :color="options.itemStyle.general.labelColor"
        class="advanced-collapsible__item"
        @submit="propChanged('itemStyle.general.labelColor', $event)"
      />
    </Collapsible>

    <Collapsible class="advanced-collapsible" :label="$t('input-style')">
      <ColorPicker
        :label="$t('background-color')"
        :color="options.itemStyle.Text.backgroundColor"
        class="advanced-collapsible__item"
        @submit="
          propChanged('itemStyle.Text.backgroundColor', $event)
          propChanged('itemStyle.Email.backgroundColor', $event)
          propChanged('itemStyle.Phone.backgroundColor', $event)
        "
      />

      <ColorPicker
        :label="$t('text-color')"
        :color="options.itemStyle.Text.textColor"
        class="advanced-collapsible__item"
        @submit="
          propChanged('itemStyle.Text.textColor', $event)
          propChanged('itemStyle.Email.textColor', $event)
          propChanged('itemStyle.Phone.textColor', $event)
        "
      />

      <ColorPicker
        :label="$t('placeholder-color')"
        :color="options.itemStyle.Text.placeholderColor"
        class="advanced-collapsible__item"
        @submit="
          propChanged('itemStyle.Text.placeholderColor', $event)
          propChanged('itemStyle.Email.placeholderColor', $event)
          propChanged('itemStyle.Phone.placeholderColor', $event)
        "
      />

      <div class="advanced-collapsible__item">
        <strong>{{ $t('text-size') }} (px)</strong>
        <PlacementNumber
          :initial="options.itemStyle.Text.textSize"
          @change="
            propChanged('itemStyle.Text.textSize', $event)
            propChanged('itemStyle.Email.textSize', $event)
            propChanged('itemStyle.Phone.textSize', $event)
          "
        />
      </div>

      <Border
        :border="options.itemStyle.Text.border"
        class="advanced-collapsible__item"
        @change="
          propChanged(`itemStyle.Text.border.${$event.field}`, $event.value)
          propChanged(`itemStyle.Email.border.${$event.field}`, $event.value)
          propChanged(`itemStyle.Phone.border.${$event.field}`, $event.value)
        "
      />

      <Spacement
        :label="$t('padding')"
        :options="options.itemStyle.Text.padding"
        class="advanced-collapsible__item"
        @change="
          propChanged(`itemStyle.Text.padding.${$event.field}`, $event.value)
          propChanged(`itemStyle.Email.padding.${$event.field}`, $event.value)
          propChanged(`itemStyle.Phone.padding.${$event.field}`, $event.value)
        "
      />
    </Collapsible>

    <Collapsible class="advanced-collapsible" :label="$t('button-style')">
      <div class="advanced-collapsible__item">
        <strong>{{ $t('font-size') }} (px)</strong>
        <PlacementNumber
          :initial="options.itemStyle.Button.text.size"
          @change="propChanged('itemStyle.Button.text.size', $event)"
        />
      </div>

      <div class="advanced-collapsible__item">
        <FontStyleButton
          text="itemStyle.Button.text"
          :values="options.itemStyle.Button.text"
          @change="propChanged($event.path, $event.value)"
        />
      </div>

      <div class="advanced-collapsible__item">
        <strong>{{ $t('text-color') }}</strong>
        <div class="color-container">
          <ColorPicker
            :label="$t('normal')"
            :color="options.itemStyle.Button.text.color"
            @submit="propChanged('itemStyle.Button.text.color', $event)"
          />
          <ColorPicker
            :label="$t('hover')"
            :color="options.itemStyle.Button.text.colorHover"
            open-direction="left"
            @submit="propChanged('itemStyle.Button.text.colorHover', $event)"
          />
        </div>
      </div>

      <div class="advanced-collapsible__item">
        <strong>{{ $t('background-color') }}</strong>
        <div class="color-container">
          <ColorPicker
            :label="$t('normal')"
            :color="options.itemStyle.Button.backgroundColor"
            @submit="propChanged('itemStyle.Button.backgroundColor', $event)"
          />
          <ColorPicker
            :label="$t('hover')"
            :color="options.itemStyle.Button.backgroundColorHover"
            open-direction="left"
            @submit="propChanged('itemStyle.Button.backgroundColorHover', $event)"
          />
        </div>
      </div>

      <Border
        :border="options.itemStyle.Button.border"
        class="advanced-collapsible__item"
        @change="propChanged(`itemStyle.Button.border.${$event.field}`, $event.value)"
      />

      <Spacement
        :label="$t('padding')"
        :options="options.itemStyle.Button.padding"
        class="advanced-collapsible__item"
        @change="propChanged(`itemStyle.Button.padding.${$event.field}`, $event.value)"
      />
    </Collapsible>
  </div>
</template>

<script>
import LayoutMixin from '@/components/sidenav/landing/shared/LayoutMixin.js'
import FontStyleMixin from '@/mixins/FontStyleMixin'

import ColorPicker from '@/shared/components/ColorPicker'
import Dropdown from '@/shared/components/Dropdown'
import Border from '@/components/sidenav/shared/Border'
import PlacementNumber from '@/shared/components/PlacementNumber'
import FontStyleButton from '@/shared/components/FontStyleButton'

export default {
  name: 'FormSideNavLayoutTab',
  i18nOptions: { keyPrefix: 'components.sidenav.landing.form.layout' },
  mixins: [LayoutMixin, FontStyleMixin],
  components: { FontStyleButton, ColorPicker, Dropdown, Border, PlacementNumber },
  methods: {
    propChanged(path, value) {
      this.updateOptions({
        id: this.id,
        path,
        value,
      })
      this.$forceUpdate()
    },
  },
}
</script>

<style lang="scss" scoped>
.advanced-collapsible {
  margin: 10px -10px 0;
  padding: 0 !important;
}

.advanced-collapsible__item {
  margin-top: 16px;
}

.color-container {
  display: flex;
  justify-content: space-around;
}
</style>
